import { useMemo, useCallback } from 'react';
import { atom, useAtom } from 'jotai';

const STORAGE_KEY_PREFIX = 'archipro:enquiry-submitted:';

/**
 * Jotai atom holding a Set of siteTreeIDs for which enquiries have been
 * submitted during this browser session. Provides real-time reactivity so
 * buttons update immediately when the EnquiryModal fires onSuccess.
 */
const enquirySubmittedSetAtom = atom<Set<number>>(new Set<number>());

/**
 * Tracks whether the user has submitted an enquiry for a given page.
 *
 * Uses a Jotai atom for instant cross-component reactivity (the tick appears
 * the moment the modal closes) and localStorage for persistence across
 * page navigations and browser sessions.
 *
 * @param siteTreeID The ID of the library item (product, professional,
 *   project, article). Pass `undefined` if not yet known.
 * @returns `hasSubmitted` — whether an enquiry has been submitted for this page.
 * @returns `markSubmitted` — call on successful enquiry submission.
 */
export function useEnquirySubmitted(siteTreeID: number | undefined) {
    const [submittedSet, setSubmittedSet] = useAtom(enquirySubmittedSetAtom);

    const hasSubmitted = useMemo(() => {
        if (siteTreeID === undefined) {
            return false;
        }

        // Check the in-memory Jotai atom first for real-time updates.
        if (submittedSet.has(siteTreeID)) {
            return true;
        }

        // Fall back to localStorage for persistence across sessions.
        if (typeof window === 'undefined') {
            return false;
        }
        try {
            return (
                localStorage.getItem(
                    STORAGE_KEY_PREFIX + String(siteTreeID),
                ) === '1'
            );
        } catch {
            return false;
        }
    }, [siteTreeID, submittedSet]);

    const markSubmitted = useCallback(() => {
        if (siteTreeID === undefined) {
            return;
        }

        // Update the Jotai atom for immediate reactivity.
        setSubmittedSet((prev: Set<number>) => {
            const next = new Set(prev);
            next.add(siteTreeID);
            return next;
        });

        // Persist to localStorage.
        if (typeof window !== 'undefined') {
            try {
                localStorage.setItem(
                    STORAGE_KEY_PREFIX + String(siteTreeID),
                    '1',
                );
            } catch {
                // localStorage may be full or unavailable — silently ignore.
            }
        }
    }, [siteTreeID, setSubmittedSet]);

    return { hasSubmitted, markSubmitted } as const;
}
